import { useMemo, useState } from "react";
import { ChefHat, Shirt, PaintRoller, Calculator } from "lucide-react";
import { Reveal } from "@/lib/motion";
import { CALC } from "@/constants/testIds";

const CONFIG = {
  kitchen: { icon: ChefHat, label: "Kitchen", unit: "sq.ft", defaultArea: 100, rates: { standard: 1400, premium: 1900, luxury: 2600 } },
  wardrobe: { icon: Shirt, label: "Wardrobe", unit: "sq.ft", defaultArea: 60, rates: { standard: 1200, premium: 1700, luxury: 2300 } },
  painting: { icon: PaintRoller, label: "Painting", unit: "sq.ft", defaultArea: 800, rates: { standard: 18, premium: 28, luxury: 42 } },
};

const QUALITIES = [
  { key: "standard", label: "Standard" },
  { key: "premium", label: "Premium" },
  { key: "luxury", label: "Luxury" },
];

const inr = (n) => "₹" + n.toLocaleString("en-IN");

export const Calculators = () => {
  const [tab, setTab] = useState("kitchen");
  const [area, setArea] = useState(CONFIG.kitchen.defaultArea);
  const [quality, setQuality] = useState("premium");

  const conf = CONFIG[tab];
  const { low, high } = useMemo(() => {
    const rate = conf.rates[quality];
    const base = Math.max(0, Number(area) || 0) * rate;
    return { low: Math.round(base * 0.92), high: Math.round(base * 1.12) };
  }, [conf, area, quality]);

  const switchTab = (k) => { setTab(k); setArea(CONFIG[k].defaultArea); };

  return (
    <section className="relative py-24 md:py-36 bg-surface">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <Reveal><div className="overline text-ink">Instant Estimate</div></Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-ink leading-[1.05]">
                Ballpark your budget in seconds.
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-5 text-ink-soft max-w-md leading-relaxed">
                Get an instant indicative cost for kitchens, wardrobes and painting. Final pricing is confirmed after a free site visit.
              </p>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <div className="rounded-3xl border border-hairline bg-canvas p-6 sm:p-9">
                <div className="flex gap-2 p-1 rounded-full bg-surface border border-hairline w-full sm:w-fit">
                  {Object.entries(CONFIG).map(([k, c]) => {
                    const Ic = c.icon;
                    return (
                      <button
                        key={k}
                        type="button"
                        data-testid={CALC.tab(k)}
                        onClick={() => switchTab(k)}
                        className={`flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-colors ${
                          tab === k ? "bg-ink text-canvas" : "text-ink hover:text-ink"
                        }`}
                      >
                        <Ic className="h-4 w-4" /> {c.label}
                      </button>
                    );
                  })}
                </div>

                <div className="mt-7">
                  <label className="text-sm font-medium text-ink">Area ({conf.unit})</label>
                  <input
                    data-testid={CALC.area}
                    type="number"
                    min="0"
                    value={area}
                    onChange={(e) => setArea(e.target.value)}
                    className="mt-2 w-full h-12 rounded-xl bg-surface border border-hairline px-4 text-ink focus:outline-none focus:border-ink"
                  />
                  <input
                    type="range" min="10" max={tab === "painting" ? 3000 : 400} value={area}
                    onChange={(e) => setArea(e.target.value)}
                    className="mt-3 w-full accent-[#C5A059]"
                  />
                </div>

                <div className="mt-6">
                  <div className="text-sm font-medium text-ink">Finish quality</div>
                  <div className="mt-2 grid grid-cols-3 gap-2">
                    {QUALITIES.map((q) => (
                      <button
                        key={q.key}
                        type="button"
                        data-testid={CALC.quality(q.key)}
                        onClick={() => setQuality(q.key)}
                        className={`py-2.5 rounded-xl text-sm font-medium border transition-colors ${
                          quality === q.key ? "bg-black text-white border-ink" : "bg-surface text-ink border-hairline hover:border-ink"
                        }`}
                      >
                        {q.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div data-testid={CALC.result} className="mt-7 rounded-2xl bg-ink text-canvas p-6 flex items-center justify-between">
                  <div>
                    <div className="text-canvas/60 text-xs uppercase tracking-wide flex items-center gap-1.5"><Calculator className="h-3.5 w-3.5" /> Estimated cost</div>
                    <div className="mt-1 font-heading text-2xl sm:text-3xl font-extrabold">{inr(low)} – {inr(high)}</div>
                  </div>
                  <a href="#contact" data-testid={CALC.cta} className="hidden sm:inline-flex bg-black text-white px-5 py-2.5 rounded-full font-semibold hover:bg-ink transition-colors">
                    Get exact quote
                  </a>
                </div>
                <a href="#contact" className="sm:hidden mt-3 inline-flex w-full justify-center bg-black text-white px-5 py-3 rounded-full font-semibold">Get exact quote</a>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
